import React, { useState } from "react";
import { motion } from "framer-motion";
import ReactPlayer from "react-player";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Play, Quote } from "lucide-react";

const testimonials = [
  {
    id: 1,
    name: "Ashok Kumar",
    project: "Villa, Adarsh Serenity",
    video: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail: "https://images.unsplash.com/photo-1650532897813-d92427f7d229?w=600&q=80",
    quote:
      "Outstanding service and attention to detail. They transformed our villa beyond expectations.",
  },
  {
    id: 2,
    name: "Rakesh Sharma",
    project: "3 BHK, Trifecta Bueno",
    video: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail: "https://images.unsplash.com/photo-1648634158203-199accfd7afc?w=600&q=80",
    quote:
      "Professional team, quality materials, and delivered exactly on time. Highly recommended!",
  },
  {
    id: 3,
    name: "Kanak Mehta",
    project: "3 BHK, Orchid Piccadilly",
    video: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail: "https://images.unsplash.com/photo-1659986480984-9b7a847168d4?w=600&q=80",
    quote:
      "The design consultation was thorough and they understood our vision perfectly.",
  },
];

export const TestimonialsSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section
      id="testimonials"
      className="section bg-[hsl(var(--brand-onyx))] text-white relative overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="noise-overlay"></div>
      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="uppercase tracking-[0.3em] text-[11px] text-[hsl(var(--brand-champagne))]/70 mb-4">
            Testimonials
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[hsl(var(--brand-champagne))]">
            Hear It From Our Clients
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">
            Let the testimonials of our clients speak volumes about the exceptional
            quality and satisfaction they've experienced.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid="testimonial-card"
            >
              <Card className="glass-card border-white/10 overflow-hidden h-full">
                <Dialog>
                  <DialogTrigger asChild>
                    <div className="relative aspect-video cursor-pointer group">
                      <img
                        src={testimonial.thumbnail}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                        <div
                          className="w-16 h-16 rounded-full bg-[hsl(var(--brand-gold))] flex items-center justify-center group-hover:scale-110 transition-transform"
                          data-testid="testimonial-play-button"
                        >
                          <Play className="w-8 h-8 text-white fill-white" />
                        </div>
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl p-0 bg-black border-[hsl(var(--brand-gold))]">
                    <div className="aspect-video">
                      <ReactPlayer
                        url={testimonial.video}
                        controls
                        playing
                        width="100%"
                        height="100%"
                      />
                    </div>
                  </DialogContent>
                </Dialog>

                <CardContent className="p-6">
                  <Quote className="w-8 h-8 text-[hsl(var(--brand-gold))] mb-4" />
                  <p className="text-white/80 text-sm leading-relaxed mb-4">
                    {testimonial.quote}
                  </p>
                  <div className="border-t border-white/10 pt-4">
                    <h4 className="font-serif-alt text-[hsl(var(--brand-champagne))] font-semibold">
                      {testimonial.name}
                    </h4>
                    <p className="text-white/60 text-sm">{testimonial.project}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
